import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { billing } from '../../api/client'
import PageHeader from '../../components/PageHeader'
import { PageLayout, Card, Badge, Modal, Skeleton } from '../../components/ui'

function statusBadgeVariant(status) {
  switch (status) {
    case 'draft': return 'default'
    case 'invoiced': return 'warning'
    case 'paid': return 'primary'
    case 'cancelled': return 'error'
    default: return 'default'
  }
}

function statusLabel(status) {
  switch (status) {
    case 'draft': return 'Draft'
    case 'invoiced': return 'Outstanding'
    case 'paid': return 'Paid'
    case 'cancelled': return 'Cancelled'
    default: return status?.replace(/_/g, ' ') || 'Unknown'
  }
}

function formatCurrency(cents) {
  if (cents == null) return '$0.00'
  const dollars = typeof cents === 'number' && cents > 100 ? cents / 100 : cents
  return `$${Number(dollars).toFixed(2)}`
}

function formatDate(dateStr) {
  if (!dateStr) return null
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatPackageType(type) {
  switch (type) {
    case 'per_session': return 'Per Session'
    case 'monthly': return 'Monthly'
    case 'one_time': return 'One Time'
    default: return type?.replace(/_/g, ' ') || '-'
  }
}

function MarkPaidModal({ isOpen, onClose, onConfirm, isPending }) {
  const [note, setNote] = useState('')

  const handleSubmit = (e) => {
    e.preventDefault()
    onConfirm(note || undefined)
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Mark as Paid" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-indices-medium">
          Mark this package as paid. You can optionally add a note about the payment.
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-indices-medium mb-1.5">
            Payment Note (optional)
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="e.g., Paid via Venmo on 3/7"
            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-dial-surface border border-gray-300 dark:border-dial-border text-gray-900 dark:text-indices-bright placeholder:text-gray-400 dark:placeholder:text-indices-muted focus:border-primary-500 dark:focus:border-lume-teal focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-lume-teal/20 resize-none"
          />
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="flex-1 py-2.5 px-4 bg-dial-elevated text-indices-bright border border-dial-border rounded-xl font-medium hover:bg-dial-surface transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="flex-1 py-2.5 px-4 bg-lume-teal text-dial-black rounded-xl font-medium hover:bg-lume-green hover:shadow-lume transition-all disabled:opacity-50"
          >
            {isPending ? 'Processing...' : 'Mark Paid'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function TimelineItem({ label, date, icon, isLast = false }) {
  if (!date) return null

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className="w-8 h-8 bg-lume-teal/20 text-lume-teal rounded-full flex items-center justify-center flex-shrink-0">
          {icon}
        </div>
        {!isLast && <div className="w-0.5 flex-1 bg-dial-border mt-1" />}
      </div>
      <div className="pb-4">
        <p className="text-sm font-medium text-gray-900 dark:text-indices-bright">{label}</p>
        <p className="text-xs text-gray-500 dark:text-indices-muted">{formatDate(date)}</p>
      </div>
    </div>
  )
}

export default function PackageDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showMarkPaid, setShowMarkPaid] = useState(false)

  const { data: pkg, isLoading } = useQuery({
    queryKey: ['billingPackage', id],
    queryFn: () => billing.get(id),
  })

  const sendInvoiceMutation = useMutation({
    mutationFn: () => billing.sendInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingPackage', id] })
      queryClient.invalidateQueries({ queryKey: ['billingPackages'] })
      queryClient.invalidateQueries({ queryKey: ['billingSummary'] })
      queryClient.invalidateQueries({ queryKey: ['outstandingCount'] })
    },
  })

  const cancelMutation = useMutation({
    mutationFn: () => billing.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billingPackage', id] })
      queryClient.invalidateQueries({ queryKey: ['billingPackages'] })
      queryClient.invalidateQueries({ queryKey: ['billingSummary'] })
      queryClient.invalidateQueries({ queryKey: ['outstandingCount'] })
    },
  })

  const markPaidMutation = useMutation({
    mutationFn: (note) => billing.markPaid(id, note),
    onSuccess: () => {
      setShowMarkPaid(false)
      queryClient.invalidateQueries({ queryKey: ['billingPackage', id] })
      queryClient.invalidateQueries({ queryKey: ['billingPackages'] })
      queryClient.invalidateQueries({ queryKey: ['billingSummary'] })
      queryClient.invalidateQueries({ queryKey: ['outstandingCount'] })
    },
  })

  const handleSendInvoice = () => {
    if (window.confirm('Send invoice to the client?')) {
      sendInvoiceMutation.mutate()
    }
  }

  const handleCancel = () => {
    if (window.confirm('Cancel this package? This action cannot be undone.')) {
      cancelMutation.mutate()
    }
  }

  if (isLoading) {
    return (
      <PageLayout>
        <PageHeader title="Package" showBack backTo="/billing" />
        <div className="p-4 space-y-4">
          <Skeleton className="h-48 rounded-xl" />
          <Skeleton className="h-32 rounded-xl" />
        </div>
      </PageLayout>
    )
  }

  if (!pkg) {
    return (
      <PageLayout>
        <PageHeader title="Package" showBack backTo="/billing" />
        <div className="p-4 text-center py-12">
          <p className="text-gray-500 dark:text-indices-muted">Package not found</p>
        </div>
      </PageLayout>
    )
  }

  const sessionsUsed = pkg.sessionsUsed || 0
  const sessionCount = pkg.sessionCount || 0
  const hasProgress = pkg.packageType === 'per_session' && sessionCount > 0
  const progressPct = hasProgress ? Math.min(100, (sessionsUsed / sessionCount) * 100) : 0

  const canSendInvoice = pkg.status === 'draft'
  const canCancel = pkg.status === 'draft' || pkg.status === 'invoiced'
  const canMarkPaid = pkg.status === 'invoiced'

  // Build timeline dates
  const timelineDates = []
  if (pkg.createdAt) timelineDates.push({ label: 'Created', date: pkg.createdAt })
  if (pkg.invoicedAt || pkg.sentAt) timelineDates.push({ label: 'Invoice Sent', date: pkg.invoicedAt || pkg.sentAt })
  if (pkg.paidAt) timelineDates.push({ label: 'Paid', date: pkg.paidAt })
  if (pkg.cancelledAt) timelineDates.push({ label: 'Cancelled', date: pkg.cancelledAt })

  return (
    <PageLayout>
      <PageHeader title="Package Details" showBack backTo="/billing" />

      <div className="px-4 py-4 space-y-4">
        {/* Package Info Card */}
        <Card>
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-indices-bright font-display truncate">
                {pkg.name}
              </h2>
              <p className="text-sm text-gray-500 dark:text-indices-muted mt-0.5">
                {pkg.clientName || 'No client'}
              </p>
            </div>
            <Badge variant={statusBadgeVariant(pkg.status)} size="lg">
              {statusLabel(pkg.status)}
            </Badge>
          </div>

          {pkg.description && (
            <p className="text-sm text-gray-600 dark:text-indices-medium mb-4">{pkg.description}</p>
          )}

          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-dial-border">
            <div>
              <p className="text-xs text-gray-500 dark:text-indices-muted">Package Type</p>
              <p className="text-sm font-medium text-gray-900 dark:text-indices-bright mt-0.5">
                {formatPackageType(pkg.packageType)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-indices-muted">Price</p>
              <p className="text-sm font-bold text-primary-600 dark:text-lume-teal mt-0.5 font-display">
                {formatCurrency(pkg.price)}
              </p>
            </div>
            {hasProgress && (
              <div>
                <p className="text-xs text-gray-500 dark:text-indices-muted">Sessions</p>
                <p className="text-sm font-medium text-gray-900 dark:text-indices-bright mt-0.5">
                  {sessionsUsed} / {sessionCount}
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Session Progress Bar */}
        {hasProgress && (
          <Card>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-900 dark:text-indices-bright font-display">Session Progress</h3>
              <span className="text-sm font-semibold text-primary-600 dark:text-lume-teal font-display">
                {Math.round(progressPct)}%
              </span>
            </div>
            <div className="w-full h-3 bg-gray-200 dark:bg-dial-elevated rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-600 dark:bg-lume-teal rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-indices-muted mt-2">
              {sessionsUsed} of {sessionCount} sessions completed
            </p>
          </Card>
        )}

        {/* Payment Timeline */}
        {timelineDates.length > 0 && (
          <Card>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-indices-bright font-display mb-4">Payment Timeline</h3>
            <div>
              {timelineDates.map((item, i) => (
                <TimelineItem
                  key={item.label}
                  label={item.label}
                  date={item.date}
                  isLast={i === timelineDates.length - 1}
                  icon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {item.label === 'Created' && (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      )}
                      {item.label === 'Invoice Sent' && (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      )}
                      {item.label === 'Paid' && (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      )}
                      {item.label === 'Cancelled' && (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      )}
                    </svg>
                  }
                />
              ))}
            </div>
          </Card>
        )}

        {/* Payment Note */}
        {pkg.paymentNote && (
          <Card>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-indices-bright font-display mb-2">Payment Note</h3>
            <p className="text-sm text-gray-600 dark:text-indices-medium">{pkg.paymentNote}</p>
          </Card>
        )}

        {/* Actions */}
        {(canSendInvoice || canCancel || canMarkPaid) && (
          <div className="space-y-2">
            {canSendInvoice && (
              <button
                onClick={handleSendInvoice}
                disabled={sendInvoiceMutation.isPending}
                className="w-full py-3 bg-lume-teal text-dial-black rounded-xl font-medium hover:bg-lume-green hover:shadow-lume transition-all disabled:opacity-50"
              >
                {sendInvoiceMutation.isPending ? 'Sending...' : 'Send Invoice'}
              </button>
            )}
            {canMarkPaid && (
              <button
                onClick={() => setShowMarkPaid(true)}
                className="w-full py-3 bg-status-success/20 text-status-success border border-status-success/30 rounded-xl font-medium hover:bg-status-success/30 transition-all"
              >
                Mark as Paid
              </button>
            )}
            {canCancel && (
              <button
                onClick={handleCancel}
                disabled={cancelMutation.isPending}
                className="w-full py-3 bg-status-error/10 text-status-error border border-status-error/20 rounded-xl font-medium hover:bg-status-error/20 transition-all disabled:opacity-50"
              >
                {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Package'}
              </button>
            )}
          </div>
        )}

        {/* Success / Error Messages */}
        {sendInvoiceMutation.isSuccess && (
          <div className="p-3 bg-status-success/10 border border-status-success/30 rounded-xl text-sm text-status-success">
            Invoice sent successfully
          </div>
        )}
        {(sendInvoiceMutation.isError || cancelMutation.isError || markPaidMutation.isError) && (
          <div className="p-3 bg-status-error/10 border border-status-error/30 rounded-xl text-sm text-status-error">
            {sendInvoiceMutation.error?.message || cancelMutation.error?.message || markPaidMutation.error?.message || 'An error occurred'}
          </div>
        )}
      </div>

      <MarkPaidModal
        isOpen={showMarkPaid}
        onClose={() => setShowMarkPaid(false)}
        onConfirm={(note) => markPaidMutation.mutate(note)}
        isPending={markPaidMutation.isPending}
      />
    </PageLayout>
  )
}
